import { memo, useMemo, useState, useRef, useEffect } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@material-ui/core";

import MaskAndValid from "utils/maskAndValid";

import { getGradeCell } from "../../shared/utils/family-grade";
import { netUnitPrice, grossUnitPrice } from "../utils/paymentConditionUtils";

export function adjustQtyToMultiple(qty, multiple) {
  if (qty <= 0) return 0;
  // Arredonda pra baixo (girar estoque não é mais a prioridade — decisão revista). Exceção: qty
  // menor que o próprio múltiplo não pode virar 0, sobe pro múltiplo mínimo.
  if (qty < multiple) return multiple;
  const remainder = qty % multiple;
  if (remainder === 0) return qty;
  return qty - remainder;
}

function ColumnHeader({ tamanho }) {
  return (
    <Typography className="matrix-prod-name" style={{ fontWeight: 700 }}>
      {tamanho}
    </Typography>
  );
}

// Ref/Múltiplo/Estoque/Preço do item — grade 2x2 exibida sob o input de cada célula
// (ver InputCell). Diferente da versão anterior (Ref/Preço fixos na coluna Item, um só
// valor por linha), agora cada tamanho mostra o dado do SEU próprio produto/SKU.
function CellInfoGrid({
  itemRef,
  multiple,
  estoque,
  price,
  discountPercent,
  encargoPercent,
}) {
  const hasDiscount = (discountPercent || 0) > 0;
  // netUnitPrice já aplica (preço * (1 + encargo%)) * (1 - desconto%) — mesma fórmula usada no
  // carrinho/finalização, pra não ter cálculo divergente entre a grade e o resto do fluxo.
  const finalPrice = netUnitPrice({ price }, discountPercent, encargoPercent);
  // Preço "de" (riscado) sempre com o encargo já embutido — nunca o preço base cru, senão pareceria
  // que o valor original já era o final sem desconto (escondendo o encargo atrás do desconto).
  const grossPrice = grossUnitPrice({ price }, encargoPercent);

  return (
    <div className="matrix-info-grid">
      <span className="matrix-info-cell">Ref: {itemRef || "—"}</span>
      <span className="matrix-info-cell">Múltiplo: {multiple}</span>
      <span className="matrix-info-cell">
        Estoque: {estoque != null ? estoque : "—"}
      </span>
      <span className="matrix-info-cell matrix-info-cell--price">
        {hasDiscount ? (
          <>
            <span className="matrix-info-price-old">
              R$ {MaskAndValid.maskPrice(grossPrice)}
            </span>{" "}
            <span className="matrix-info-price-new">
              R$ {MaskAndValid.maskPrice(finalPrice)}
            </span>
          </>
        ) : (
          // Sem desconto: preço único já com encargo embutido — nunca o preço base cru.
          <>Preço: R$ {MaskAndValid.maskPrice(finalPrice)}</>
        )}
      </span>
    </div>
  );
}

// Memoizada e recebendo `value` como primitivo (não o objeto tempGridState inteiro):
// com onUpdateQty estável, só a célula cujo valor mudou re-renderiza a cada tecla,
// em vez de todas as células da grade.
const InputCell = memo(function InputCell({
  compoundId,
  value,
  multiple,
  theme,
  onUpdateQty,
  itemRef,
  estoque,
  price,
  discountPercent,
  encargoPercent,
}) {
  // Digitação livre em estado local — o arredondamento pro múltiplo (onUpdateQty)
  // só é aplicado ao sair do campo, não a cada tecla. Antes, arredondar em cima do
  // valor parcial digitado travava a digitação (ex.: "12" virava "3" logo depois do
  // "1", e o próximo caractere digitado ia para o final de "3" em vez de completar "12").
  const [draft, setDraft] = useState(value ?? "");
  const focusedRef = useRef(false);

  useEffect(() => {
    if (!focusedRef.current) setDraft(value ?? "");
  }, [value]);

  return (
    <div className="matrix-cell-stack">
      {/* input nativo em vez de TextField do MUI: uma grade pode ter centenas de
          células e cada TextField monta ~6 componentes aninhados, o que tornava a
          abertura do modal lenta. O estilo vem da classe .matrix-input. */}
      <input
        type="number"
        inputMode="numeric"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onFocus={(e) => {
          focusedRef.current = true;
          e.target.style.outline = `2px solid ${theme.primary}`;
        }}
        onBlur={(e) => {
          focusedRef.current = false;
          e.target.style.outline = "none";
          // Calcula e reflete o valor arredondado direto aqui, sem depender do useEffect
          // acima re-sincronizar: quando o valor arredondado NOVO é igual ao valor que já
          // estava salvo (ex.: campo tinha 50, usuário digita 24, arredonda de volta pra 50),
          // a prop `value` não muda de 50 para 50 sob a ótica do React, o efeito não dispara
          // de novo e o campo ficava mostrando "24" mesmo com o estado real já sendo 50.
          const parsed = parseInt(e.target.value, 10) || 0;
          const rounded = adjustQtyToMultiple(parsed, multiple);
          setDraft(rounded > 0 ? String(rounded) : "");
          onUpdateQty(compoundId, e.target.value, multiple);
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") e.target.blur();
        }}
        className="matrix-input"
        min="0"
        step={multiple}
      />
      <CellInfoGrid
        itemRef={itemRef}
        multiple={multiple}
        estoque={estoque}
        price={price}
        discountPercent={discountPercent}
        encargoPercent={encargoPercent}
      />
    </div>
  );
});

export default function GradeMatrix({
  fam,
  familyKey,
  theme,
  tempGridState,
  onUpdateQty,
  discountForProduct,
  encargoForProduct,
}) {
  const groups = fam?.grade?.groups || [];
  const sizes = fam?.grade?.sizes || [];
  const [activeRowKey, setActiveRowKey] = useState(null);

  const rows = useMemo(
    () =>
      groups.map((group) => {
        const cells = sizes.map((tamanho) => {
          const product = getGradeCell(fam, group.linha, tamanho);
          return {
            tamanho,
            product,
            compoundId: product ? `${familyKey}_${product.id}` : null,
            discountPercent: product ? discountForProduct(product) : 0,
            encargoPercent: product ? encargoForProduct(product) : 0,
          };
        });
        return {
          linha: group.linha,
          cells,
        };
      }),
    [fam, familyKey, groups, sizes, discountForProduct, encargoForProduct],
  );

  return (
    <Box className="grade-venda-layout">
      <Box className="grade-venda-grid-panel">
        <Table className="matrix-table grade-venda-grid-table">
          <TableHead className="matrix-header">
            <TableRow>
              <TableCell className="matrix-header-col matrix-item-col">
                Item
              </TableCell>
              {sizes.map((tamanho) => (
                <TableCell key={tamanho} className="matrix-header-col">
                  <ColumnHeader tamanho={tamanho} />
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow
                key={row.linha}
                className={`matrix-row grade-grid-row ${
                  activeRowKey === row.linha ? "grade-grid-row--active" : ""
                }`}
                onClick={() => setActiveRowKey(row.linha)}
              >
                <TableCell className="matrix-item-cell">
                  <Typography className="matrix-item-name">
                    {row.linha}
                  </Typography>
                </TableCell>
                {row.cells.map(
                  ({
                    tamanho,
                    product,
                    compoundId,
                    discountPercent,
                    encargoPercent,
                  }) => (
                    <TableCell key={tamanho} className="matrix-input-cell">
                      {compoundId ? (
                        <InputCell
                          compoundId={compoundId}
                          value={tempGridState[compoundId] || ""}
                          multiple={product.multiple || 1}
                          theme={theme}
                          onUpdateQty={onUpdateQty}
                          itemRef={product.ref}
                          estoque={product.estoque}
                          price={product.price}
                          discountPercent={discountPercent}
                          encargoPercent={encargoPercent}
                        />
                      ) : (
                        <Typography variant="caption" color="textPrimary">
                          —
                        </Typography>
                      )}
                    </TableCell>
                  ),
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    </Box>
  );
}
